import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import { useGetBranches } from "src/api/generic";
import {
  useCreateFoodicsBranchMapping,
  useDeleteFoodicsBranchMapping,
} from "src/api/integrations/foodics";
import type { FoodicsBranch, FoodicsBranchMapping } from "src/types/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";

type BranchMappingModalProps = {
  open: boolean;
  handleClose: () => void;
  foodicsBranches: FoodicsBranch[];
  foodicsBranchesLoading: boolean;
  mappings: FoodicsBranchMapping[];
  refetchMappings: () => void;
};

export const BranchMappingModal = ({
  open,
  handleClose,
  foodicsBranches,
  foodicsBranchesLoading,
  mappings,
  refetchMappings,
}: BranchMappingModalProps) => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  // Pending Jazr branch choice per not-yet-mapped Foodics branch id
  const [selections, setSelections] = useState<Record<string, number | "">>(
    {}
  );
  const [isSaving, setIsSaving] = useState(false);

  const { data: jazrBranchesData, isLoading: jazrBranchesLoading } =
    useGetBranches();
  const { mutateAsync: createMapping } = useCreateFoodicsBranchMapping();
  const { mutateAsync: deleteMapping, status: deleteStatus } =
    useDeleteFoodicsBranchMapping();

  const jazrBranches = jazrBranchesData?.data ?? [];
  const currentLang = i18n.language || "en";

  const getFoodicsBranchName = (branch: FoodicsBranch): string => {
    if (currentLang === "ar" && branch.name_localized)
      return branch.name_localized;
    return branch.name || "-";
  };

  const getJazrBranchName = (
    name: string | { en: string; ar: string } | undefined
  ): string => {
    if (!name) return "-";
    if (typeof name === "string") return name;
    return name[currentLang as keyof typeof name] || name.en || "-";
  };

  const mappingByFoodicsId = new Map(
    mappings.map((mapping) => [mapping.external_id, mapping])
  );
  const mappedJazrBranchIds = mappings.map((mapping) => mapping.internal_id);
  const selectedJazrBranchIds = Object.values(selections).filter(
    (value): value is number => value !== ""
  );
  const allMapped =
    foodicsBranches.length > 0 &&
    foodicsBranches.every((branch) => mappingByFoodicsId.has(branch.id));
  const hasPendingSelections = selectedJazrBranchIds.length > 0;
  // A Jazr branch links to exactly one Foodics branch, so whenever there are more
  // Foodics branches than Jazr branches some can never be mapped. Say how many are
  // being left out instead of showing empty dropdowns on the leftover rows.
  const allJazrBranchesMapped =
    jazrBranches.length > 0 &&
    jazrBranches.every((branch) => mappedJazrBranchIds.includes(branch.id));
  const unmappedFoodicsCount = foodicsBranches.filter(
    (branch) => !mappingByFoodicsId.has(branch.id)
  ).length;
  const hasFewerJazrBranches =
    jazrBranches.length > 0 && foodicsBranches.length > jazrBranches.length;

  const availableJazrBranchesFor = (foodicsBranchId: string) =>
    jazrBranches.filter(
      (branch) =>
        !mappedJazrBranchIds.includes(branch.id) &&
        (!selectedJazrBranchIds.includes(branch.id) ||
          selections[foodicsBranchId] === branch.id)
    );

  const handleSelectionChange = (
    foodicsBranchId: string,
    jazrBranchId: number | ""
  ) => {
    setSelections((prev) => ({ ...prev, [foodicsBranchId]: jazrBranchId }));
  };

  const handleSave = async () => {
    const pendingEntries = Object.entries(selections).filter(
      ([foodicsBranchId, jazrBranchId]) =>
        jazrBranchId !== "" && !mappingByFoodicsId.has(foodicsBranchId)
    );
    if (pendingEntries.length === 0) return;
    setIsSaving(true);
    try {
      for (const [foodicsBranchId, jazrBranchId] of pendingEntries) {
        const foodicsBranch = foodicsBranches.find(
          (branch) => branch.id === foodicsBranchId
        );
        await createMapping({
          internal_id: jazrBranchId as number,
          external_id: foodicsBranchId,
          external_code: foodicsBranch?.reference,
          external_name: foodicsBranch
            ? getFoodicsBranchName(foodicsBranch)
            : undefined,
        });
      }
      setNotifications([
        ...notifications,
        { type: "success", message: t("mappings.saveSuccess") },
      ]);
      setSelections({});
      refetchMappings();
    } catch {
      setNotifications([
        ...notifications,
        { type: "error", message: t("mappings.saveError") },
      ]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (mapping: FoodicsBranchMapping) => {
    try {
      await deleteMapping(mapping.id);
      setNotifications([
        ...notifications,
        { type: "success", message: t("mappings.deleteSuccess") },
      ]);
      refetchMappings();
    } catch {
      setNotifications([
        ...notifications,
        { type: "error", message: t("mappings.deleteError") },
      ]);
    }
  };

  const onClose = () => {
    setSelections({});
    handleClose();
  };

  const isLoading = foodicsBranchesLoading || jazrBranchesLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography fontSize={18} fontWeight={600} color="#101828">
          {t("foodicsWorkspace.branchMappingModal.title")}
        </Typography>
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.branchMappingModal.subtitle")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!isLoading && allMapped && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("foodicsWorkspace.branchMappingModal.allMapped")}
          </Alert>
        )}
        {!isLoading && !allMapped && allJazrBranchesMapped && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("foodicsWorkspace.branchMappingModal.allJazrBranchesMapped", {
              unmappedCount: unmappedFoodicsCount,
            })}
          </Alert>
        )}
        {!isLoading && !allMapped && !allJazrBranchesMapped && hasFewerJazrBranches && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("foodicsWorkspace.branchMappingModal.fewerJazrBranches", {
              jazrCount: jazrBranches.length,
              foodicsCount: foodicsBranches.length,
            })}
          </Alert>
        )}
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {[
                  t("foodicsWorkspace.branchMappingModal.foodicsBranch"),
                  t("foodicsWorkspace.branchMappingModal.jazrBranch"),
                  t("mappings.table.actions"),
                ].map((item) => (
                  <TableCell
                    key={item}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      color: "#475467",
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableLoadingSkeleton rowsLength={4} cellsLength={3} />
            ) : (
              <TableBody>
                {foodicsBranches.map((branch) => {
                  const mapping = mappingByFoodicsId.get(branch.id);
                  const availableJazrBranches = availableJazrBranchesFor(
                    branch.id
                  );
                  return (
                    <TableRow key={branch.id}>
                      <TableCell>
                        <Typography fontSize={14} color="#101828">
                          {getFoodicsBranchName(branch)}
                        </Typography>
                        <Typography fontSize={12} color="#667085">
                          {branch.reference || branch.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {mapping ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography fontSize={14} color="#101828">
                              {getJazrBranchName(mapping.internal_name) ||
                                mapping.internal_id}
                            </Typography>
                            <Chip
                              size="small"
                              color="success"
                              label={t(
                                "foodicsWorkspace.branchMappingModal.mapped"
                              )}
                            />
                          </Stack>
                        ) : availableJazrBranches.length === 0 ? (
                          <Typography fontSize={13} color="#667085">
                            {t(
                              "foodicsWorkspace.branchMappingModal.noJazrBranchAvailable"
                            )}
                          </Typography>
                        ) : (
                          <FormControl size="small" fullWidth>
                            <InputLabel>
                              {t(
                                "foodicsWorkspace.branchMappingModal.selectJazrBranch"
                              )}
                            </InputLabel>
                            <Select
                              value={selections[branch.id] ?? ""}
                              label={t(
                                "foodicsWorkspace.branchMappingModal.selectJazrBranch"
                              )}
                              onChange={(e) =>
                                handleSelectionChange(
                                  branch.id,
                                  e.target.value as number | ""
                                )
                              }
                            >
                              <MenuItem value="">
                                <em>—</em>
                              </MenuItem>
                              {availableJazrBranches.map((jazrBranch) => (
                                <MenuItem
                                  key={jazrBranch.id}
                                  value={jazrBranch.id}
                                >
                                  {getJazrBranchName(jazrBranch.name)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell width={64}>
                        {mapping && (
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleteStatus === "pending"}
                            onClick={() => handleDelete(mapping)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
          {!isLoading && foodicsBranches.length === 0 && <NoData />}
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{generalT("cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasPendingSelections || isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("foodicsWorkspace.branchMappingModal.saveMappings")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
